import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Sparkles, Check, X, RefreshCw, Edit3, Save, Trash2, Linkedin,
  Instagram, Twitter, Facebook, FileText, Loader2, Inbox, Archive,
  Hash, Pause as PauseIcon, ChevronLeft, Zap, ImagePlus, Film, XCircle,
  Wand2, Eye, FolderOpen,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_ICONS = {
  linkedin: Linkedin,
  instagram: Instagram,
  x: Twitter,
  twitter: Twitter,
  facebook: Facebook,
  blog: FileText,
};

const CHANNEL_COLORS = {
  linkedin: 'text-[#0A66C2]',
  instagram: 'text-[#E4405F]',
  x: 'text-zinc-900',
  twitter: 'text-sky-500',
  facebook: 'text-[#1877F2]',
  blog: 'text-emerald-600',
};

const StatusFilter = ({ active, counts, onChange }) => {
  const tabs = [
    { id: 'pending', label: 'Inbox', icon: Inbox },
    { id: 'approved', label: 'Approved', icon: Check },
    { id: 'snoozed', label: 'Snoozed', icon: PauseIcon },
    { id: 'rejected', label: 'Archive', icon: Archive },
  ];
  return (
    <div className="flex items-center gap-1 bg-white border rounded-lg p-1" data-testid="content-queue-filter">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            active === t.id ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
          data-testid={`content-queue-tab-${t.id}`}
        >
          <t.icon className="h-3.5 w-3.5" />
          {t.label}
          {counts?.[t.id] > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              active === t.id ? 'bg-white/20' : 'bg-gray-100'
            }`}>{counts[t.id]}</span>
          )}
        </button>
      ))}
    </div>
  );
};

const DraftCard = ({ draft, onApprove, onReject, onSnooze, onSave, onDelete, onAttachMedia, onDetachMedia, onGenerateHero, onOpenPreview, onOpenLibrary }) => {
  const [editing, setEditing] = useState(false);
  const [headline, setHeadline] = useState(draft.headline || '');
  const [body, setBody] = useState(draft.body || '');
  const [hashtags, setHashtags] = useState((draft.hashtags || []).join(' '));
  const [uploading, setUploading] = useState(false);
  const [generatingHero, setGeneratingHero] = useState(false);
  const Icon = CHANNEL_ICONS[draft.channel] || FileText;
  const cls = CHANNEL_COLORS[draft.channel] || 'text-gray-500';
  const mediaUrls = draft.media_urls || [];
  const canAttach = draft.status === 'pending' && mediaUrls.length < 10;

  const saveEdits = async () => {
    await onSave(draft.id, {
      headline: headline.trim(),
      body: body.trim(),
      hashtags: hashtags.split(/\s+/).filter(Boolean).map(h => h.startsWith('#') ? h : `#${h}`),
    });
    setEditing(false);
  };

  const isVideoUrl = (u) => /\.(mp4|mov|webm)(\?|$)/i.test(u || '');

  const generateHero = async () => {
    setGeneratingHero(true);
    try { await onGenerateHero(draft.id); } finally { setGeneratingHero(false); }
  };

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (uploaded.length + mediaUrls.length >= 10) {
        toast.error('Max 10 media files per draft');
        break;
      }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await axios.post(`${API}/api/content-queue/upload-media`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        uploaded.push(r.data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    if (uploaded.length > 0) {
      await onAttachMedia(draft.id, uploaded);
    }
    setUploading(false);
  };

  return (
    <Card
      className="border-gray-200 hover:border-emerald-300 transition-colors group"
      data-testid={`content-draft-${draft.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center ${cls}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{draft.channel}</div>
              <div className="text-[10px] text-gray-400">{draft.format} · {new Date(draft.created_at).toLocaleDateString()}</div>
            </div>
          </div>
          {draft.status === 'pending' && !editing && (
            <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700">
              <Edit3 className="h-4 w-4" />
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Headline" className="font-semibold" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="text-sm" />
            <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#one #two" className="text-xs font-mono" />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={saveEdits} className="bg-emerald-600 hover:bg-emerald-700">
                <Save className="h-3.5 w-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-semibold text-gray-900 mb-2 leading-snug">{draft.headline}</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{draft.body}</p>
            {draft.hashtags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {draft.hashtags.map((h, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 font-mono">
                    <Hash className="h-2.5 w-2.5 mr-0.5" />{h.replace(/^#/, '')}
                  </Badge>
                ))}
              </div>
            )}

            {/* v3.3: media attachments — thumbnails + attach button */}
            {mediaUrls.length > 0 && (
              <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid={`media-strip-${draft.id}`}>
                {mediaUrls.map((u, i) => (
                  <div key={i} className="relative group/media aspect-square rounded overflow-hidden bg-gray-100 border border-gray-200">
                    {isVideoUrl(u) ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                        <Film className="h-5 w-5" />
                        <span className="text-[9px] mt-0.5">video</span>
                      </div>
                    ) : (
                      <img src={u} alt="attachment" className="w-full h-full object-cover" loading="lazy" />
                    )}
                    {draft.status === 'pending' && (
                      <button
                        onClick={() => onDetachMedia(draft.id, u)}
                        className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-white rounded-full p-0.5 text-rose-600 opacity-0 group-hover/media:opacity-100 transition-opacity"
                        title="Remove"
                        data-testid={`detach-media-${draft.id}-${i}`}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canAttach && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {mediaUrls.length === 0 && (
                  <button
                    onClick={generateHero}
                    disabled={generatingHero || uploading}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-50"
                    data-testid={`gen-hero-${draft.id}`}
                    title="Generate an on-brand hero image based on this draft's headline"
                  >
                    {generatingHero ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    <span>{generatingHero ? 'Generating…' : 'Generate hero image'}</span>
                  </button>
                )}
                <button
                  onClick={() => onOpenLibrary(draft.id)}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  data-testid={`from-library-${draft.id}`}
                  title="Pick media from your Creative Studio library"
                >
                  <FolderOpen className="h-3 w-3" />
                  <span>From library</span>
                </button>
                <label className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 cursor-pointer" data-testid={`attach-media-${draft.id}`}>
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
                  <span>{uploading ? 'Uploading…' : (mediaUrls.length > 0 ? `Add more (${mediaUrls.length}/10)` : 'Upload')}</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                    multiple
                    disabled={uploading}
                    onChange={handleFilePick}
                  />
                </label>
                <button
                  onClick={() => onOpenPreview(draft)}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 ml-auto"
                  data-testid={`preview-${draft.id}`}
                  title="See how this post will look when published"
                >
                  <Eye className="h-3 w-3" />
                  <span>Preview</span>
                </button>
              </div>
            )}
            {!canAttach && draft.status !== 'pending' && mediaUrls.length > 0 && (
              <button
                onClick={() => onOpenPreview(draft)}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gray-600 hover:text-gray-800"
                data-testid={`preview-${draft.id}`}
              >
                <Eye className="h-3 w-3" /> Preview post
              </button>
            )}

            {draft.reasoning && (
              <div className="mt-3 pt-3 border-t border-dashed border-gray-200 text-xs text-gray-500 italic">
                <Sparkles className="h-3 w-3 inline mr-1" /> {draft.reasoning}
              </div>
            )}

            {draft.status === 'pending' && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReject(draft.id)}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  data-testid={`reject-${draft.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSnooze(draft.id)}
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  data-testid={`snooze-${draft.id}`}
                >
                  <PauseIcon className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => onApprove(draft.id)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`approve-${draft.id}`}
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
            )}
            {draft.status !== 'pending' && (
              <div className="mt-4 flex items-center justify-between">
                <Badge variant="outline" className={
                  draft.status === 'approved' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' :
                  draft.status === 'rejected' ? 'border-rose-200 text-rose-700 bg-rose-50' :
                  'border-amber-200 text-amber-700 bg-amber-50'
                }>
                  {draft.status}
                </Badge>
                <button
                  onClick={() => onDelete(draft.id)}
                  className="text-gray-400 hover:text-rose-600"
                  data-testid={`delete-${draft.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default function ContentQueuePage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [refilling, setRefilling] = useState(false);
  const [autoPublish, setAutoPublish] = useState({ enabled: false, delay_minutes: 15 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, ap] = await Promise.all([
        axios.get(`${API}/api/content-queue`, { params: { status } }),
        axios.get(`${API}/api/content-queue/auto-publish`),
      ]);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
      setAutoPublish({
        enabled: !!ap.data?.enabled,
        delay_minutes: ap.data?.delay_minutes ?? 15,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load content queue');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const refill = async () => {
    setRefilling(true);
    try {
      const r = await axios.post(`${API}/api/content-queue/refill`);
      if (r.data?.skipped) {
        toast.info(`Already have ${r.data.pending} pending drafts — try clearing some first.`);
      } else {
        toast.success(`Generated ${r.data?.inserted || 0} new drafts`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate drafts. Make sure your Brand Kit / Brand DNA is filled.');
    } finally {
      setRefilling(false);
    }
  };

  const decide = async (id, action) => {
    try {
      const r = await axios.post(`${API}/api/content-queue/${id}/decide`, { action });
      setItems((prev) => prev.filter((d) => d.id !== id));
      setCounts((c) => ({
        ...c,
        [status]: Math.max(0, (c[status] || 1) - 1),
        [action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'snoozed']:
          (c[action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'snoozed'] || 0) + 1,
      }));
      if (r.data?.auto_published) {
        toast.success(`Approved + scheduled to publish at ${new Date(r.data.scheduled_at).toLocaleString()}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update draft');
    }
  };

  const toggleAutoPublish = async (enabled) => {
    setAutoPublish((p) => ({ ...p, enabled }));
    try {
      await axios.put(`${API}/api/content-queue/auto-publish`, {
        enabled,
        delay_minutes: autoPublish.delay_minutes,
      });
      toast.success(enabled ? 'Auto-publish ON — approved drafts go straight to the social scheduler.' : 'Auto-publish OFF');
    } catch (e) {
      setAutoPublish((p) => ({ ...p, enabled: !enabled }));
      toast.error('Could not update setting');
    }
  };

  const saveEdits = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/content-queue/${id}`, patch);
      setItems((prev) => prev.map((d) => (d.id === id ? r.data.draft : d)));
      toast.success('Saved');
    } catch (e) {
      toast.error('Could not save');
    }
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Remove this draft permanently?')) return;
    try {
      await axios.delete(`${API}/api/content-queue/${id}`);
      setItems((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  // v3.3 (2026-07-01): media attach / detach — updates the local list in place
  // so the thumbnails appear immediately without a full refetch.
  const attachMedia = async (id, urls) => {
    try {
      const r = await axios.post(`${API}/api/content-queue/${id}/attach-media`, { urls });
      setItems((prev) => prev.map((d) => (d.id === id ? { ...d, media_urls: r.data.media_urls } : d)));
      toast.success(`${urls.length} file${urls.length > 1 ? 's' : ''} attached`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    }
  };

  const detachMedia = async (id, url) => {
    try {
      await axios.delete(`${API}/api/content-queue/${id}/media`, { params: { url } });
      setItems((prev) => prev.map((d) => (d.id === id ? { ...d, media_urls: (d.media_urls || []).filter((u) => u !== url) } : d)));
    } catch (e) {
      toast.error('Could not remove attachment');
    }
  };

  // v3.4 (2026-07-01): auto-generate an on-brand hero image for this draft.
  const generateHero = async (id) => {
    try {
      const r = await axios.post(`${API}/api/content-queue/${id}/generate-hero`);
      if (r.data?.media_urls) {
        setItems((prev) => prev.map((d) => (d.id === id ? { ...d, media_urls: r.data.media_urls } : d)));
        toast.success('Hero image generated & attached');
      } else if (r.data?.task_id) {
        toast('Image generation started — it will appear shortly.', { icon: '⏳' });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate image');
    }
  };

  // Post preview dialog state
  const [previewDraft, setPreviewDraft] = useState(null);
  // Library picker dialog state
  const [libraryPicker, setLibraryPicker] = useState({ open: false, draftId: null, items: [], loading: false });
  const openLibrary = async (draftId) => {
    setLibraryPicker({ open: true, draftId, items: [], loading: true });
    try {
      const r = await axios.get(`${API}/api/creative-studio/library`, { params: { limit: 60 } });
      const items = (r.data?.items || r.data || []).filter((it) => it.url).slice(0, 60);
      setLibraryPicker({ open: true, draftId, items, loading: false });
    } catch {
      setLibraryPicker({ open: true, draftId, items: [], loading: false });
      toast.error('Could not load library');
    }
  };
  const pickFromLibrary = async (url) => {
    if (!libraryPicker.draftId) return;
    try {
      const r = await axios.post(`${API}/api/content-queue/${libraryPicker.draftId}/attach-media`, { urls: [url] });
      setItems((prev) => prev.map((d) => (d.id === libraryPicker.draftId ? { ...d, media_urls: r.data.media_urls } : d)));
      toast.success('Media attached from library');
      setLibraryPicker({ open: false, draftId: null, items: [], loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto page-shell" data-testid="content-queue-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-emerald-500" /> Content Queue<PageInfoTooltip pageKey="content-queue" />
          </h1>
          <p className="text-gray-500 mt-1">
            AI-drafted, on-brand content waiting for your green light. Approve, edit, snooze.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusFilter active={status} counts={counts} onChange={setStatus} />
          <Button
            onClick={refill}
            disabled={refilling}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="content-queue-refill-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refilling ? 'animate-spin' : ''}`} />
            {refilling ? 'Drafting…' : 'Generate drafts'}
          </Button>
        </div>
      </div>

      {/* Auto-publish toggle */}
      <Card className={`mb-6 transition-colors ${autoPublish.enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200'}`}>
        <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${autoPublish.enabled ? 'bg-emerald-100' : 'bg-gray-100'}`}>
              <Zap className={`h-5 w-5 ${autoPublish.enabled ? 'text-emerald-700' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Auto-publish on approve</h3>
              <p className="text-xs text-gray-600 max-w-2xl">
                When ON: approving a draft schedules it directly into the social scheduler {autoPublish.delay_minutes} min later — zero-touch from idea to live post. (Blog drafts are exempt — they always need a human publish.)
              </p>
            </div>
          </div>
          <Switch
            checked={autoPublish.enabled}
            onCheckedChange={toggleAutoPublish}
            data-testid="auto-publish-toggle"
          />
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="py-16 text-center" data-testid="content-queue-empty">
            <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center">
              <Inbox className="h-7 w-7 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {status === 'pending' ? 'No drafts waiting' : `No ${status} items`}
            </h3>
            <p className="text-sm text-gray-500 mb-5 max-w-md mx-auto">
              {status === 'pending'
                ? 'Drafts are auto-generated every 6 hours from your Brand DNA and products. You can also trigger one now.'
                : `Items you ${status === 'approved' ? 'approve' : status === 'rejected' ? 'reject' : 'snooze'} land here.`}
            </p>
            {status === 'pending' && (
              <Button onClick={refill} disabled={refilling} className="bg-emerald-600 hover:bg-emerald-700">
                <RefreshCw className={`h-4 w-4 mr-2 ${refilling ? 'animate-spin' : ''}`} />
                Generate now
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="content-queue-list">
          {items.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              onApprove={(id) => decide(id, 'approve')}
              onReject={(id) => decide(id, 'reject')}
              onSnooze={(id) => decide(id, 'snooze')}
              onSave={saveEdits}
              onDelete={removeDraft}
              onAttachMedia={attachMedia}
              onDetachMedia={detachMedia}
              onGenerateHero={generateHero}
              onOpenPreview={setPreviewDraft}
              onOpenLibrary={openLibrary}
            />
          ))}
        </div>
      )}

      {/* v3.4: Post preview — shows how the draft will look on the target platform */}
      <Dialog open={!!previewDraft} onOpenChange={(o) => { if (!o) setPreviewDraft(null); }}>
        <DialogContent className="max-w-md" data-testid="content-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm text-gray-600 flex items-center gap-2">
              <Eye className="h-4 w-4" /> Post preview
              {previewDraft && (
                <Badge variant="outline" className="text-[10px] font-normal capitalize border-gray-300">
                  {previewDraft.channel}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewDraft && <PostPreview draft={previewDraft} />}
        </DialogContent>
      </Dialog>

      {/* v3.4: Library picker — reuses Creative Studio library items */}
      <Dialog open={libraryPicker.open} onOpenChange={(o) => { if (!o) setLibraryPicker({ open: false, draftId: null, items: [], loading: false }); }}>
        <DialogContent className="max-w-3xl" data-testid="content-library-picker">
          <DialogHeader>
            <DialogTitle className="text-sm text-gray-700 flex items-center gap-2">
              <FolderOpen className="h-4 w-4" /> Pick from Creative Studio library
            </DialogTitle>
          </DialogHeader>
          {libraryPicker.loading ? (
            <div className="py-16 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : libraryPicker.items.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500">
              Your Creative Studio library is empty. Generate images/videos there first, then come back.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
              {libraryPicker.items.map((it, i) => {
                const isVid = /\.(mp4|mov|webm)(\?|$)/i.test(it.url || '');
                return (
                  <button
                    key={i}
                    onClick={() => pickFromLibrary(it.url)}
                    className="relative aspect-square rounded overflow-hidden bg-gray-100 border border-gray-200 hover:border-emerald-400 hover:ring-2 hover:ring-emerald-200 transition-all group"
                    data-testid={`library-item-${i}`}
                  >
                    {isVid ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                        <Film className="h-6 w-6" />
                        <span className="text-[10px] mt-0.5">video</span>
                      </div>
                    ) : (
                      <img src={it.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] p-0.5 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                      Attach
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Post preview — mock how the draft will look on each channel ─────
function PostPreview({ draft }) {
  const ch = (draft.channel || 'linkedin').toLowerCase();
  const media = draft.media_urls?.[0];
  const isVid = /\.(mp4|mov|webm)(\?|$)/i.test(media || '');
  const body = (draft.body || '') + (draft.hashtags?.length ? '\n\n' + draft.hashtags.map((h) => h.startsWith('#') ? h : `#${h}`).join(' ') : '');
  const brand = draft.brand_snapshot?.company_name || 'Your Brand';

  if (ch === 'instagram') {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-w-sm mx-auto" data-testid="preview-instagram">
        <div className="flex items-center gap-2 p-2 border-b border-gray-100">
          <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-bold">{brand.charAt(0)}</div>
          <div className="text-xs font-semibold text-gray-900">{brand.toLowerCase().replace(/\s+/g, '')}</div>
        </div>
        <div className="aspect-square bg-gray-100 flex items-center justify-center">
          {media ? (isVid ? <Film className="h-10 w-10 text-gray-400" /> : <img src={media} alt="" className="w-full h-full object-cover" />) : <ImagePlus className="h-10 w-10 text-gray-300" />}
        </div>
        <div className="p-3 text-xs">
          <div className="font-semibold text-gray-900 mb-1">{draft.headline}</div>
          <div className="text-gray-700 whitespace-pre-wrap">{body}</div>
        </div>
      </div>
    );
  }
  if (ch === 'x' || ch === 'twitter') {
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-white max-w-md mx-auto" data-testid="preview-x">
        <div className="flex items-start gap-2">
          <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center text-white text-xs font-bold shrink-0">{brand.charAt(0)}</div>
          <div className="min-w-0 flex-1">
            <div className="text-xs"><span className="font-bold text-gray-900">{brand}</span> <span className="text-gray-500">@{brand.toLowerCase().replace(/\s+/g, '')} · now</span></div>
            <div className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{draft.headline}{body ? '\n\n' + body : ''}</div>
            {media && (
              <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                {isVid ? <div className="aspect-video bg-gray-100 flex items-center justify-center"><Film className="h-8 w-8 text-gray-400" /></div> : <img src={media} alt="" className="w-full max-h-72 object-cover" />}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (ch === 'facebook') {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-w-md mx-auto" data-testid="preview-facebook">
        <div className="p-3 flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#0866FF] flex items-center justify-center text-white text-xs font-bold">{brand.charAt(0)}</div>
          <div className="text-xs"><div className="font-semibold text-gray-900">{brand}</div><div className="text-gray-500">Just now · 🌐</div></div>
        </div>
        <div className="px-3 pb-2 text-sm text-gray-900 whitespace-pre-wrap"><div className="font-semibold mb-1">{draft.headline}</div>{body}</div>
        {media && (isVid ? <div className="aspect-video bg-gray-100 flex items-center justify-center"><Film className="h-10 w-10 text-gray-400" /></div> : <img src={media} alt="" className="w-full max-h-80 object-cover" />)}
      </div>
    );
  }
  // Default: LinkedIn
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-w-md mx-auto" data-testid="preview-linkedin">
      <div className="p-3 flex items-start gap-2">
        <div className="w-11 h-11 rounded-full bg-[#0A66C2] flex items-center justify-center text-white text-sm font-bold">{brand.charAt(0)}</div>
        <div className="text-xs">
          <div className="font-semibold text-gray-900">{brand}</div>
          <div className="text-gray-500">Sponsored · <Linkedin className="h-3 w-3 inline" /></div>
        </div>
      </div>
      <div className="px-3 pb-2 text-sm text-gray-900 whitespace-pre-wrap"><div className="font-semibold mb-1">{draft.headline}</div>{body}</div>
      {media && (isVid ? <div className="aspect-video bg-gray-100 flex items-center justify-center"><Film className="h-10 w-10 text-gray-400" /></div> : <img src={media} alt="" className="w-full max-h-80 object-cover" />)}
    </div>
  );
}
